import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IClinique, ISetSearchParams } from 'components/core/types'
import { AppDispatch } from 'components/store/state/store'
import { updateCurrentClinique, updateCurrentStep } from 'components/core/state/actions'
import Button from 'components/ui/abstract/Button'
import Styles from 'components/ui/Styles'
import { css, StyleSheet } from 'aphrodite'
import ucfirst from 'utils/ucfirst'
import constants from 'utils/constants'
import TimeIcon from 'assets/images/icon-open.svg'
import GoogleRating from 'components/ui/GoogleRating'
import { getCliniqueOpenCloseDetails } from 'components/core/state/selectors'
import { faPhone } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useResponsive from 'hooks/useResponsive'

const mapDispatch = (dispatch: AppDispatch) => ({
  updateClinique: (cliniqueId: number, setSearchParams: ISetSearchParams) => {
    dispatch(updateCurrentClinique(cliniqueId, setSearchParams))
  },
  updateStep: (step: number, setSearchParams: ISetSearchParams) => {
    dispatch(updateCurrentStep(step, setSearchParams))
  },
})
const connector = connect(null, mapDispatch)

interface ICliniqueBlock extends ConnectedProps<typeof connector> {
  clinique: IClinique,
}

const CliniqueBlock = ({ clinique, updateClinique, updateStep }: ICliniqueBlock) => {
  const [, setSearchParams] = useSearchParams()
  const { compareTag } = useResponsive()
  const { t } = useTranslation()
  const { status } = getCliniqueOpenCloseDetails(clinique)
  const [isOpen, setIsOpen] = useState(status)
  const [tick, setTick] = useState(null)
  useEffect(() => {
    setTick(setInterval(() => {
      const { status: newStatus } = getCliniqueOpenCloseDetails(clinique)
      setIsOpen(newStatus)
    }, 60000))
    return () => {
      clearInterval(tick)
    }
  }, [isOpen])
  const handleClick = (cliniqueId: number) => {
    updateClinique(cliniqueId, setSearchParams)
    updateStep(3, setSearchParams)
  }
  const {
    title,
    acf: {
      phone,
      subtitle,
      banner_clinique: {
        image: {
          url,
        } = {},
      } = {},
    },
    google_datas,
    id,
    link,
  } = clinique
  const {
    result: {
      rating = null,
      user_ratings_total = null,
      opening_hours: {
        periods,
      } = {},
    } = {},
  } = google_datas

  const styles = StyleSheet.create({
    button: {
      display: 'flex',
      backgroundColor: constants.colors.green,
      fontSize: compareTag('sm') ? 13 : 9,
      padding: '7px 14px',
      lineHeight: '24px',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      width: 'fit-content',
    },
    contentContainer: {
      padding: '23px 33px 76px',
    },
    image: {
      backgroundImage: `url(${url})`,
      aspectRatio: '4/3',
      minHeight: compareTag('md') ? 262.5 : 225,
    },
    phone: {
      letterSpacing: '0.01em',
      fontSize: 22,
    },
    phoneIcon: {
      width: 22,
    },
    title: {
      lineHeight: 1.225,
      margin: '5px 0 13px',
    },
  })
  const openCloseContainerStyle = [
    'tw-flex',
    'tw-items-center',
    'tw-pt-px',
    'tw-pb-0.5',
    'tw-pl-1',
    'tw-pr-1.5',
    'tw-absolute',
    'tw-top-2.5',
    'tw-left-2.5',
    'tw-bg-white',
    'tw-tracking-wide',
    'tw-text-dark-green',
    'tw-text-lg',
  ].join(' ')
  const titleStyle = [
    css(styles.title),
    'tw-uppercase',
    'tw-font-bold',
    'tw-text-base md:tw-text-2xl',
    'tw-tracking-widest',
    'tw-text-dark-green',
  ].join(' ')
  return (
    <div className="tw-flex tw-flex-col tw-max-w-full">
      <div className={`tw-relative tw-bg-center tw-bg-cover ${css(styles.image)}`}>
        {!!periods && (
          <div className={openCloseContainerStyle}>
            <img className="tw-mr-1" src={TimeIcon} alt="open-close-status" />
            {t(isOpen ? 'open' : 'closed').toUpperCase()}
          </div>
        )}
      </div>
      <div className={`${css(styles.contentContainer)} tw-relative tw-bg-white tw-h-full`}>
        <div className="tw-text-background tw-text-lg tw-leading-5">{subtitle}</div>
        <div className={titleStyle}>
          {title}
        </div>
        <div className="reviews">
          {!!google_datas && rating && <GoogleRating rating={rating} />}
          {!!google_datas && user_ratings_total && (
            <div className="tw-text-green tw-text-sm">
              {user_ratings_total}
              {' reviews'}
            </div>
          )}
        </div>
        {!!phone && (
          <div className="tw-flex tw-gap-x-2.5 tw-mt-3.5 tw-mb-1 tw-text-background tw-items-center">
            <FontAwesomeIcon icon={faPhone} className={css(styles.phoneIcon)} color={constants.colors.background} />
            <a href={`tel:${phone}`} className={css(styles.phone)}>
              {phone}
            </a>
          </div>
        )}
        <div className="tw-flex tw-gap-6 tw-absolute tw-left-8 tw-bottom-5">
          <Button className={css(Styles.button, styles.button)} onClick={() => handleClick(id)}>
            {t('appointment')}
          </Button>
          <a
            href={link}
            className={`${css(Styles.button, styles.button)} primary-btn tw-whitespace-nowrap`}
            target="_blank"
            rel="noreferrer"
          >
            {ucfirst(t('infos'))}
          </a>
        </div>
      </div>
    </div>
  )
}
export default connector(CliniqueBlock)
